/**
 * DiModelBindingPage — Compliance · Document Intelligence model bindings.
 *
 * Per doc-type capture slot, picks which Azure Document Intelligence model
 * reads it (SystemSettings `nra.scan.modelMap.<slot>`), plus the classifier
 * model + its confidence threshold (`nra.classifier.{modelId,confidenceThreshold}`).
 * This is the §2.7 "leverage the Azure DI training" surface — it decides
 * whether a slot uses a GA prebuilt (e.g. prebuilt-idDocument) or a PCB
 * custom-trained neural model (e.g. pcb-visa-v2).
 *
 * Wiring (fully shipped — no stub): reads via client.retail.config.systemSettingsRows();
 * writes via client.retail.admin.systemSettings.upsert(). Model choices come from a
 * curated catalog of known DI models (slot-2-azure-di.md §E, MS Learn DI v4.0 GA
 * 2024-11-30, WebFetched 2026-05-27) — a dropdown, never a free-text model id.
 *
 * 5 admin-quality bars: wired (real SystemSettings read/write) · configurable
 * (per-slot model + threshold) · makes sense (GA / Custom / Unconfigured pills,
 * humanized slot names, description hints) · modern control (model dropdown +
 * bounded numeric threshold, not free text) · no-breaking (dropdown constrains
 * values; threshold clamped 0–1; the live bound value always stays selectable).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  type SystemSettingUpsertBody,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { NumberField } from '@/components/FormField';

const MODEL_MAP_PREFIX = 'nra.scan.modelMap.';
const KEY_CLASSIFIER_MODEL = 'nra.classifier.modelId';
const KEY_CONFIDENCE = 'nra.classifier.confidenceThreshold';

type ModelKind = 'ga' | 'custom' | 'unconfigured' | 'classifier';

interface KnownModel {
  id: string;
  kind: ModelKind;
  blurb: string;
}

/**
 * Curated DI model catalog. GA = Azure prebuilt (v4.0, 2024-11-30 GA); Custom
 * = PCB-trained neural; Unconfigured = bound but no corpus trained yet.
 * Source: slot-2-azure-di.md §E + seed/52,59,62. Not Core-Director fields —
 * these are Azure DI model identifiers, surfaced as a dropdown per the task.
 */
const KNOWN_MODELS: ReadonlyArray<KnownModel> = [
  // ── GA prebuilt ──
  { id: 'prebuilt-idDocument', kind: 'ga', blurb: 'IDs / passports (bio page) — excludes visas' },
  { id: 'prebuilt-invoice', kind: 'ga', blurb: 'Address proof (invoice)' },
  { id: 'prebuilt-payStub.us', kind: 'ga', blurb: 'US paystub — employer + employee address/name' },
  { id: 'prebuilt-bankStatement', kind: 'ga', blurb: 'US bank statement (verify exact id at call time)' },
  { id: 'prebuilt-check.us', kind: 'ga', blurb: 'US check — holder name/address + routing/account' },
  { id: 'prebuilt-marriageCertificate.us', kind: 'ga', blurb: 'Name verification (maiden → married)' },
  { id: 'prebuilt-tax.us.1040', kind: 'ga', blurb: 'US 1040 personal tax' },
  { id: 'prebuilt-mortgage.us.1003', kind: 'ga', blurb: 'US mortgage 1003 (rich borrower/address)' },
  { id: 'prebuilt-mortgage.us.closingDisclosure', kind: 'ga', blurb: 'US mortgage closing disclosure' },
  // ── PCB custom neural ──
  { id: 'pcb-i94-v2', kind: 'custom', blurb: 'I-94 (Templates A/B + D/S sentinel)' },
  { id: 'pcb-visa-v2', kind: 'custom', blurb: 'Visa (prebuilt-idDocument can’t read visas)' },
  { id: 'pcb-i20-v2', kind: 'custom', blurb: 'I-20 (DHS/ICE OMB 1653-0038)' },
  { id: 'pcb-ds2019-v2', kind: 'custom', blurb: 'DS-2019 (DOS exchange-visitor)' },
  // ── Bound but untrained ──
  { id: 'pcb-ssn-v1', kind: 'unconfigured', blurb: 'SSN card — bound but no corpus; returns scanner-not-configured (slot-2 C5)' },
  // ── Classifier ──
  { id: 'pcb-nra-classifier-v1', kind: 'classifier', blurb: 'Routes a drop to a doc-type before extraction' },
];

const KNOWN_BY_ID = new Map(KNOWN_MODELS.map((m) => [m.id, m]));

/** Preferred display order for the doc-type slots. Unknown slots sort after, alpha. */
const SLOT_ORDER = [
  'passport', 'visa', 'green-card', 'ead', 'i94', 'i20', 'ds2019', 'ssn', 'address-proof',
];

interface SystemSettingRow {
  settingKey: string;
  settingValue: string | null;
  dataType: string;
  categoryCode: string;
  description: string | null;
  isActive: boolean;
}

function humanizeSlot(key: string): string {
  const slot = key.slice(MODEL_MAP_PREFIX.length);
  switch (slot) {
    case 'i94': return 'I-94';
    case 'i20': return 'I-20';
    case 'ds2019': return 'DS-2019';
    case 'ssn': return 'SSN card';
    case 'ead': return 'EAD (I-766)';
    case 'green-card': return 'Green card (I-551)';
    case 'address-proof': return 'Address proof';
    default: return slot.charAt(0).toUpperCase() + slot.slice(1);
  }
}

function kindPill(kind: ModelKind | 'unknown'): JSX.Element {
  switch (kind) {
    case 'ga': return <Pill tone="ok">GA prebuilt</Pill>;
    case 'custom': return <Pill tone="info">PCB custom</Pill>;
    case 'unconfigured': return <Pill tone="warn">unconfigured</Pill>;
    case 'classifier': return <Pill tone="info">classifier</Pill>;
    default: return <Pill tone="muted">unverified</Pill>;
  }
}

function classify(modelId: string | null): ModelKind | 'unknown' {
  if (!modelId) return 'unknown';
  return KNOWN_BY_ID.get(modelId)?.kind ?? 'unknown';
}

export function DiModelBindingPage(): JSX.Element {
  const client = useApiClient();

  const [rows, setRows] = useState<ReadonlyArray<SystemSettingRow> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [thresholdDraft, setThresholdDraft] = useState<number | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const resp = await client.retail.config.systemSettingsRows({ signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        if (resp.success && Array.isArray(resp.data)) {
          const all = resp.data as unknown as ReadonlyArray<SystemSettingRow>;
          const mine = all.filter(
            (r) =>
              r.settingKey.startsWith(MODEL_MAP_PREFIX) ||
              r.settingKey === KEY_CLASSIFIER_MODEL ||
              r.settingKey === KEY_CONFIDENCE,
          );
          setRows(mine);
          const conf = mine.find((r) => r.settingKey === KEY_CONFIDENCE);
          setThresholdDraft(conf?.settingValue != null ? Number(conf.settingValue) : null);
        } else {
          setRows([]);
          setLoadError(resp.error ?? 'Failed to load DI model bindings.');
        }
      } catch (e) {
        if (ctrl.signal.aborted) return;
        setRows([]);
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => ctrl.abort();
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const modelSlots = useMemo(() => {
    const slots = (rows ?? []).filter((r) => r.settingKey.startsWith(MODEL_MAP_PREFIX));
    return [...slots].sort((a, b) => {
      const sa = a.settingKey.slice(MODEL_MAP_PREFIX.length);
      const sb = b.settingKey.slice(MODEL_MAP_PREFIX.length);
      const ia = SLOT_ORDER.indexOf(sa);
      const ib = SLOT_ORDER.indexOf(sb);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return sa.localeCompare(sb);
    });
  }, [rows]);

  const classifierRow = (rows ?? []).find((r) => r.settingKey === KEY_CLASSIFIER_MODEL);
  const confidenceRow = (rows ?? []).find((r) => r.settingKey === KEY_CONFIDENCE);

  const upsertValue = useCallback(
    async (row: SystemSettingRow, newValue: string) => {
      setActionError(null);
      setPending((s) => new Set(s).add(row.settingKey));
      try {
        const body: SystemSettingUpsertBody = {
          settingKey: row.settingKey,
          settingValue: newValue,
          dataType: row.dataType,
        };
        const resp = await client.retail.admin.systemSettings.upsert(ADMIN_AUTH_TOKEN_SENTINEL, body);
        if (!resp.success) throw new Error(resp.error ?? 'upsert returned success=false');
        setToast(`Updated ${row.settingKey} → ${newValue}.`);
        refresh();
      } catch (e) {
        setActionError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      } finally {
        setPending((s) => {
          const next = new Set(s);
          next.delete(row.settingKey);
          return next;
        });
      }
    },
    [client, refresh],
  );

  // Build the dropdown option list, always including the live value even if unknown.
  const optionsFor = useCallback((current: string | null): JSX.Element[] => {
    const ids = new Set(KNOWN_MODELS.map((m) => m.id));
    const opts: JSX.Element[] = [];
    const ga = KNOWN_MODELS.filter((m) => m.kind === 'ga');
    const custom = KNOWN_MODELS.filter((m) => m.kind === 'custom' || m.kind === 'unconfigured');
    if (current && !ids.has(current)) {
      opts.push(
        <option key={current} value={current}>
          {current} (current — unrecognized)
        </option>,
      );
    }
    opts.push(<optgroup key="ga" label="Azure prebuilt (GA)">{ga.map((m) => (
      <option key={m.id} value={m.id}>{m.id} — {m.blurb}</option>
    ))}</optgroup>);
    opts.push(<optgroup key="custom" label="PCB custom neural">{custom.map((m) => (
      <option key={m.id} value={m.id}>{m.id} — {m.blurb}</option>
    ))}</optgroup>);
    return opts;
  }, []);

  const thresholdDirty =
    confidenceRow != null &&
    thresholdDraft != null &&
    String(thresholdDraft) !== (confidenceRow.settingValue ?? '');

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load DI model bindings: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading DI model bindings…" />;

  return (
    <>
      {toast && (
        <Banner tone="info">
          {toast} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}
      {actionError && <Banner tone="err">{actionError}</Banner>}

      <Banner tone="info">
        Bind each capture slot to the Azure Document Intelligence model that reads it.{' '}
        <strong>GA prebuilt</strong> models are Azure-hosted; <strong>PCB custom</strong> models were
        neural-trained on PCB corpora (visas / I-94 / I-20 / DS-2019, which the prebuilt can’t read).
        An <strong>unconfigured</strong> binding (e.g. SSN) points at a model with no trained corpus
        and will return scanner-not-configured until trained. Changing a binding takes effect on the
        next scan — no redeploy.
      </Banner>

      {modelSlots.length === 0 && (
        <Card title="Document-type model bindings">
          <p className="empty">
            No <code>nra.scan.modelMap.*</code> settings found. Run seed 52 / 59 / 62 to populate the
            model map.
          </p>
        </Card>
      )}

      {modelSlots.length > 0 && (
        <Card title={`Document-type model bindings — ${modelSlots.length}`}>
          <div className="data-table__wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Doc-type slot</th>
                  <th>Bound model</th>
                  <th>Kind</th>
                  <th>Setting key</th>
                </tr>
              </thead>
              <tbody>
                {modelSlots.map((row) => {
                  const busy = pending.has(row.settingKey);
                  const kind = classify(row.settingValue);
                  return (
                    <tr key={row.settingKey}>
                      <td>
                        <div className="cell-strong">{humanizeSlot(row.settingKey)}</div>
                        {row.description && <div className="cell-desc">{row.description}</div>}
                      </td>
                      <td>
                        <select
                          className="form-input di-model__select"
                          value={row.settingValue ?? ''}
                          disabled={busy}
                          onChange={(e) => void upsertValue(row, e.target.value)}
                          aria-label={`Model for ${humanizeSlot(row.settingKey)}`}
                        >
                          {optionsFor(row.settingValue)}
                        </select>
                      </td>
                      <td>{kindPill(kind)}</td>
                      <td>
                        <code className="cell-mono">{row.settingKey}</code>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card title="Classifier">
        {classifierRow ? (
          <div className="data-table__wrap">
            <table className="data-table">
              <tbody>
                <tr>
                  <td className="cell-strong" style={{ width: 220 }}>
                    Classifier model
                  </td>
                  <td>
                    <select
                      className="form-input di-model__select"
                      value={classifierRow.settingValue ?? ''}
                      disabled={pending.has(classifierRow.settingKey)}
                      onChange={(e) => void upsertValue(classifierRow, e.target.value)}
                      aria-label="Classifier model"
                    >
                      {(classifierRow.settingValue &&
                        !KNOWN_BY_ID.has(classifierRow.settingValue)) && (
                        <option value={classifierRow.settingValue}>
                          {classifierRow.settingValue} (current — unrecognized)
                        </option>
                      )}
                      {KNOWN_MODELS.filter((m) => m.kind === 'classifier').map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.id} — {m.blurb}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>{kindPill('classifier')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <p className="empty">
            <code>{KEY_CLASSIFIER_MODEL}</code> not set.
          </p>
        )}

        {confidenceRow && (
          <div className="di-threshold">
            <NumberField
              label="Classifier confidence threshold"
              value={thresholdDraft}
              onChange={setThresholdDraft}
              min={0}
              max={1}
              step={0.01}
              hint="0–1. Below this, the capture UI shows a “what is this?” picker before extraction. Seed default 0.85."
            />
            <div className="di-threshold__actions">
              <Button
                variant="primary"
                disabled={
                  pending.has(KEY_CONFIDENCE) ||
                  !thresholdDirty ||
                  thresholdDraft == null ||
                  thresholdDraft < 0 ||
                  thresholdDraft > 1
                }
                onClick={() =>
                  void upsertValue(confidenceRow, String(thresholdDraft ?? ''))
                }
              >
                {pending.has(KEY_CONFIDENCE) ? 'Saving…' : 'Save threshold'}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </>
  );
}
